import React, { useCallback, useContext, useMemo, useState } from 'react'
import { ServerExperiments } from '@/experimentation'
import { getWebClient } from '@/services/ApolloClient'
import {
  getGiveawaySchedule,
  getValidPublicDiscountsForRelease,
  GiveawaySchedule,
  UserGuildDiscounts,
  ValidPublicDiscounts,
} from '@/services/Theatrical/queries'
import { ReactFCC } from '@/types/react'
import { useLocale } from '@/utils/LocaleUtil'
import { TheatricalPromotion } from './types'

export interface TheatricalPromotionContextValues {
  promo: TheatricalPromotion | null
  experiments?: ServerExperiments
  publicDiscounts: ValidPublicDiscounts
  giveawaySchedule: GiveawaySchedule | null
  refetchPublicDiscounts: () => Promise<void>
  refetchGiveawaySchedule: () => Promise<void>
}

const TheatricalPromotionContext = React.createContext<TheatricalPromotionContextValues>({
  promo: null,
  experiments: undefined,
  publicDiscounts: {
    earlyBirdDiscount: null,
    noFeesDiscount: null,
  },
  giveawaySchedule: null,
  refetchPublicDiscounts: () => Promise.resolve(),
  refetchGiveawaySchedule: () => Promise.resolve(),
})

export const useTheatricalPromotionContext = () => {
  const ctx = useContext(TheatricalPromotionContext)
  if (!ctx) {
    throw new Error('useTheatricalPromotionContext must be used within <TheatricalPromotionContextProvider>')
  }
  return ctx
}

interface TheatricalPromotionContextProviderProps {
  theatricalSlug: string
  experiments?: ServerExperiments
  publicDiscounts: ValidPublicDiscounts
  guildCodes: UserGuildDiscounts
  giveawaySchedule: GiveawaySchedule | null
  promo: TheatricalPromotion | null
}

export const TheatricalPromotionContextProvider: ReactFCC<TheatricalPromotionContextProviderProps> = ({
  children,
  experiments,
  publicDiscounts: initialPublicDiscounts,
  giveawaySchedule: initialGiveawaySchedule,
  promo,
}) => {
  const { locale } = useLocale()
  const [publicDiscounts, setPublicDiscounts] = useState<ValidPublicDiscounts>(initialPublicDiscounts)
  const [giveawaySchedule, setGiveawaySchedule] = useState<GiveawaySchedule | null>(initialGiveawaySchedule)
  const client = getWebClient({ locale })

  const refetchPublicDiscounts = useCallback(async () => {
    if (!promo?.theatricalSlug) return
    const res = await getValidPublicDiscountsForRelease(client, promo.theatricalSlug)
    if (res) {
      setPublicDiscounts(res)
    }
  }, [client, promo?.theatricalSlug])

  const refetchGiveawaySchedule = useCallback(async () => {
    if (!promo) return
    const res = await getGiveawaySchedule(client, promo.theatricalSlug)
    if (res) {
      setGiveawaySchedule(res)
    }
  }, [client, promo])

  const values = useMemo(() => {
    return {
      promo,
      experiments,
      publicDiscounts,
      refetchPublicDiscounts,
      giveawaySchedule,
      refetchGiveawaySchedule,
    }
  }, [promo, experiments, publicDiscounts, giveawaySchedule, refetchPublicDiscounts, refetchGiveawaySchedule])

  return <TheatricalPromotionContext.Provider value={values}>{children}</TheatricalPromotionContext.Provider>
}
